import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Select from "react-select";
import axios from "axios";
import { STATO_META } from "../mocks/mezziDisponibiliMock";
import RotateDevice from "../components/RotateDevice/RotateDevice";
import "./MezziDisponibiliLive.css";
import Lf23Loader from "../components/Lf23Loader";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3001/api";
const POLL_INTERVAL_MS = 30_000;
const TIMELINE_PAST_HOURS = 6;
const TIMELINE_FUTURE_HOURS = 12;

const formatTime = (date) =>
    date.toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit", second: "2-digit" });

// I cantieri arrivano in MAIUSCOLO dal DWH ("MERCATALE", "GAGGIO MONTANO"). A frontend
// li mostriamo in Title Case (es. "Gaggio Montano") solo per leggibilità: il valore
// originale è quello usato per filtri/lookup, va trasformato solo in fase di display.
const toTitleCase = (s) => {
    if (s == null) return s;
    return String(s)
        .toLowerCase()
        .replace(/\b\p{L}/gu, (c) => c.toUpperCase());
};

// Costruisce i 18 slot della timeline (6 passati + 12 futuri) partendo dall'array
// `timeline` pre-calcolato dal backend, allineato in modo che timeline[6] == "now".
// Le etichette orarie sono derivate dall'orologio locale per coerenza con lastUpdate.
const buildTimeline = (mezzo, now) => {
    const nowHour = now.getHours();
    const timeline = Array.isArray(mezzo.timeline) ? mezzo.timeline : [];
    const slots = [];
    for (let i = -TIMELINE_PAST_HOURS; i < TIMELINE_FUTURE_HOURS; i++) {
        const hour = (nowHour + i + 24) % 24;
        const idx = i + TIMELINE_PAST_HOURS;
        const stato = timeline[idx] || "disponibile";
        slots.push({ hour, stato, isNow: i === 0 });
    }
    return slots;
};

const MezziDisponibiliLive = ({ isStandaloneMode = false }) => {
    const navigate = useNavigate();

    const [sediData, setSediData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [lastUpdate, setLastUpdate] = useState(() => new Date());
    const [collapsedSedi, setCollapsedSedi] = useState({});
    const [showInfo, setShowInfo] = useState(false);
    const [fullscreenSede, setFullscreenSede] = useState(null);
    const [showFilters, setShowFilters] = useState(false);
    // Pending filters (UI state, editabile liberamente)
    const defaultStati = [{ value: "disponibile", label: STATO_META.disponibile.label }];
    const [selectedStati, setSelectedStati] = useState(defaultStati);
    const [selectedSedi, setSelectedSedi] = useState([]);
    const [selectedTipologie, setSelectedTipologie] = useState([]);
    const [selectedSottotipologie, setSelectedSottotipologie] = useState([]);
    // Applied filters (usati per filtrare i dati, aggiornati al click di Applica)
    const [appliedStati, setAppliedStati] = useState(defaultStati);
    const [appliedSedi, setAppliedSedi] = useState([]);
    const [appliedTipologie, setAppliedTipologie] = useState([]);
    const [appliedSottotipologie, setAppliedSottotipologie] = useState([]);
    // Chip filters (sempre visibili, applicati immediatamente al click)
    const [selectedCantieriChip, setSelectedCantieriChip] = useState([]);
    const [selectedTipologieChip, setSelectedTipologieChip] = useState([]);

    const fetchData = useCallback(async () => {
        try {
            const res = await axios.get(`${API_URL}/mezzi-disponibili/live`);
            setSediData(Array.isArray(res.data) ? res.data : []);
            setError(null);
            setLastUpdate(new Date());
        } catch (err) {
            console.error("Errore fetch mezzi disponibili:", err);
            setError(err.response?.data?.error || err.message || "Errore nel recupero dati");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchData();
        const id = setInterval(fetchData, POLL_INTERVAL_MS);
        return () => clearInterval(id);
    }, [fetchData]);

    useEffect(() => {
        const handleEsc = (e) => {
            if (e.key === "Escape" && fullscreenSede) setFullscreenSede(null);
        };
        document.addEventListener("keydown", handleEsc);
        return () => document.removeEventListener("keydown", handleEsc);
    }, [fullscreenSede]);

    const toggleSede = (sede) =>
        setCollapsedSedi((prev) => ({ ...prev, [sede]: !prev[sede] }));

    const toggleFullscreen = (sede) =>
        setFullscreenSede((curr) => (curr === sede ? null : sede));

    const statoOptions = useMemo(
        () => Object.entries(STATO_META).map(([value, meta]) => ({ value, label: meta.label })),
        []
    );
    const sedeOptions = useMemo(
        () => sediData.map((s) => ({ value: s.sede, label: toTitleCase(s.sede) })),
        [sediData]
    );
    const tipologiaOptions = useMemo(() => {
        const set = new Set();
        sediData.forEach((s) => s.mezzi.forEach((m) => m.tipologia && set.add(m.tipologia)));
        return Array.from(set).sort().map((t) => ({ value: t, label: t }));
    }, [sediData]);
    const sottotipologiaOptions = useMemo(() => {
        const set = new Set();
        sediData.forEach((s) => s.mezzi.forEach((m) => m.sottotipologia && set.add(m.sottotipologia)));
        return Array.from(set).sort().map((t) => ({ value: t, label: t }));
    }, [sediData]);

    // Valori unici per le chip Cantieri/Tipologia (sempre derivati dai dati grezzi sediData).
    const cantieriUnici = useMemo(
        () => [...new Set(sediData.map((s) => s.sede).filter(Boolean))].sort(),
        [sediData]
    );
    const tipologieUniche = useMemo(
        () =>
            [
                ...new Set(
                    sediData.flatMap((s) => (s.mezzi || []).map((m) => m.tipologia)).filter(Boolean)
                ),
            ].sort(),
        [sediData]
    );

    // Inizializzazione "tutto selezionato" al primo arrivo dei dati: le chip partono
    // attive su ogni cantiere/tipologia disponibile (stesso pattern di ManutenzioneAutomezzi).
    useEffect(() => {
        if (
            sediData.length > 0 &&
            selectedCantieriChip.length === 0 &&
            selectedTipologieChip.length === 0
        ) {
            setSelectedCantieriChip(cantieriUnici);
            setSelectedTipologieChip(tipologieUniche);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sediData]);

    // I fermi vengono SEMPRE raccolti in un'unica tabella "Mezzi Fermi" in cima alla lista
    // (no raggruppamento per cantiere) ogni volta che devono essere mostrati: cioe' quando
    // "fermo" e' tra gli stati selezionati, oppure quando il filtro stato e' vuoto (= tutti).
    // Stessa logica per gli altri stati: vanno mostrati nei cantieri se selezionati o se
    // il filtro stato e' vuoto.
    const showFermi = appliedStati.length === 0 || appliedStati.some((o) => o.value === "fermo");
    const showOtherStati = appliedStati.length === 0 || appliedStati.some((o) => o.value !== "fermo");

    const sedi = useMemo(() => {
        const statiFiltro = appliedStati.map((o) => o.value);
        const sediFiltro = appliedSedi.map((o) => o.value);
        const tipologieFiltro = appliedTipologie.map((o) => o.value);
        const sottotipologieFiltro = appliedSottotipologie.map((o) => o.value);
        return sediData
            .filter((s) => sediFiltro.length === 0 || sediFiltro.includes(s.sede))
            .filter((s) => selectedCantieriChip.includes(s.sede))
            .map((s) => {
                const mezziFiltrati = s.mezzi.filter((m) => {
                    if (statiFiltro.length > 0 && !statiFiltro.includes(m.stato)) return false;
                    // Se la tabella unica fermi e' attiva insieme ai cantieri, escludo i fermi
                    // dai gruppi per cantiere: appariranno nella tabella unica in cima.
                    if (showFermi && showOtherStati && m.stato === "fermo") return false;
                    if (tipologieFiltro.length > 0 && !tipologieFiltro.includes(m.tipologia)) return false;
                    if (sottotipologieFiltro.length > 0 && !sottotipologieFiltro.includes(m.sottotipologia)) return false;
                    if (!selectedTipologieChip.includes(m.tipologia)) return false;
                    return true;
                });
                return {
                    ...s,
                    mezzi: mezziFiltrati,
                    disponibili: mezziFiltrati.filter((m) => m.stato === "disponibile").length,
                    totali: mezziFiltrati.length,
                };
            })
            .filter((s) => {
                // Quando i fermi vengono scorporati, le sedi che resterebbero vuote vengono nascoste
                // anche senza filtro stato esplicito (altrimenti vedremmo card di cantiere vuote).
                if (showFermi && showOtherStati) return s.totali > 0;
                return s.totali > 0 || statiFiltro.length === 0;
            });
    }, [sediData, appliedStati, appliedSedi, appliedTipologie, appliedSottotipologie, showFermi, showOtherStati, selectedCantieriChip, selectedTipologieChip]);

    const totaleDisponibili = sedi.reduce((sum, s) => sum + s.disponibili, 0);
    const totaleMezzi = sedi.reduce((sum, s) => sum + s.totali, 0);

    const displayGroups = useMemo(() => {
        if (!showFermi) return sedi;

        // Estraggo i fermi rispettando i filtri non-stato (sede / tipologia / sottotipologia).
        const sediFiltro = appliedSedi.map((o) => o.value);
        const tipologieFiltro = appliedTipologie.map((o) => o.value);
        const sottotipologieFiltro = appliedSottotipologie.map((o) => o.value);
        const fermi = [];
        for (const s of sediData) {
            if (sediFiltro.length > 0 && !sediFiltro.includes(s.sede)) continue;
            if (!selectedCantieriChip.includes(s.sede)) continue;
            for (const m of s.mezzi) {
                if (m.stato !== "fermo") continue;
                if (tipologieFiltro.length > 0 && !tipologieFiltro.includes(m.tipologia)) continue;
                if (sottotipologieFiltro.length > 0 && !sottotipologieFiltro.includes(m.sottotipologia)) continue;
                if (!selectedTipologieChip.includes(m.tipologia)) continue;
                fermi.push({ ...m, sede: s.sede });
            }
        }
        fermi.sort(
            (a, b) =>
                String(a.sede).localeCompare(String(b.sede)) ||
                String(a.targa).localeCompare(String(b.targa))
        );

        const groups = [];
        if (fermi.length > 0) {
            groups.push({
                sede: "Mezzi Fermi",
                mezzi: fermi,
                totali: fermi.length,
                disponibili: 0,
                isFermiTable: true,
            });
        }
        if (showOtherStati) {
            groups.push(...sedi);
        }
        return groups;
    }, [sedi, sediData, showFermi, showOtherStati, appliedSedi, appliedTipologie, appliedSottotipologie, selectedCantieriChip, selectedTipologieChip]);

    const filtriAttivi =
        appliedStati.length > 0 ||
        appliedSedi.length > 0 ||
        appliedTipologie.length > 0 ||
        appliedSottotipologie.length > 0;

    const handleApplyFiltri = () => {
        setAppliedStati(selectedStati);
        setAppliedSedi(selectedSedi);
        setAppliedTipologie(selectedTipologie);
        setAppliedSottotipologie(selectedSottotipologie);
    };

    const handleResetFiltri = () => {
        setSelectedStati(defaultStati);
        setSelectedSedi([]);
        setSelectedTipologie([]);
        setSelectedSottotipologie([]);
        setAppliedStati(defaultStati);
        setAppliedSedi([]);
        setAppliedTipologie([]);
        setAppliedSottotipologie([]);
    };

    return (
        <div className="mezzi-disp-page">
            <div className="page-header">
                <div className="header-content">
                    {!isStandaloneMode && (
                        <button className="back-button" onClick={() => navigate("/produttivita")}>
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M19 12H5M12 19l-7-7 7-7" />
                            </svg>
                            Indietro
                        </button>
                    )}
                    <div className="header-title">
                        <div className="header-title-row">
                            <h2>Mezzi Disponibili Live</h2>
                            <button
                                className="info-button-modern"
                                onClick={() => setShowInfo(true)}
                                title="Informazioni"
                            >
                                <span className="info-icon-text">i</span>
                            </button>
                        </div>
                        <p className="header-subtitle">
                            Mezzi realmente disponibili per sostituzioni operative, per sede/cantiere.
                        </p>
                    </div>
                </div>
            </div>

            {/* Info Modal */}
            {showInfo && (
                <div className="modal-overlay" onClick={() => setShowInfo(false)}>
                    <div className="modal-container" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <div className="modal-icon">
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <circle cx="12" cy="12" r="10"></circle>
                                    <line x1="12" y1="16" x2="12" y2="12"></line>
                                    <line x1="12" y1="8" x2="12.01" y2="8"></line>
                                </svg>
                            </div>
                            <h3>Informazioni Mezzi Disponibili Live</h3>
                            <button className="modal-close" onClick={() => setShowInfo(false)}>
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <line x1="18" y1="6" x2="6" y2="18"></line>
                                    <line x1="6" y1="6" x2="18" y2="18"></line>
                                </svg>
                            </button>
                        </div>
                        <div className="modal-body">
                            <div className="modal-section">
                                <h4>Cosa fa questa pagina?</h4>
                                <p>
                                    Pannello live pensato per i <strong>capisquadra</strong>: mostra, per ogni <strong>sede/cantiere</strong>, i mezzi
                                    realmente disponibili per sostituzioni operative. Incrocia lo <strong>stato del mezzo</strong> e la
                                    <strong> pianificazione turni</strong> per escludere mezzi non utilizzabili.
                                </p>
                                <p style={{ marginTop: '10px' }}>
                                    ⚠️ Questa è una <strong>versione mockup</strong> con dati simulati, utile a validare il layout prima di collegare
                                    le fonti reali (Interacta, WebFleet, turni).
                                </p>
                            </div>

                            <div className="modal-section">
                                <h4>Stati del mezzo</h4>
                                <ul style={{ marginLeft: '20px', marginTop: '10px', lineHeight: '1.8' }}>
                                    <li><strong>Disponibile:</strong> mezzo pronto all'uso — nessun fermo attivo, non impegnato in turno</li>
                                    <li><strong>Fermo:</strong> segnalazione o manutenzione attiva (fonte: Interacta)</li>
                                    <li><strong>In turno:</strong> già assegnato a un turno/operatore nella fascia oraria corrente</li>
                                </ul>
                            </div>

                            <div className="modal-section">
                                <h4>Aggiornamento live</h4>
                                <p>
                                    Il pannello si aggiorna automaticamente ogni <strong>30 secondi</strong>. È possibile forzare un refresh manuale
                                    tramite l'apposito pulsante. Il timestamp "Ultimo agg." indica l'orario dell'ultima lettura.
                                </p>
                            </div>

                            <div className="modal-section">
                                <h4>Layout e navigazione</h4>
                                <p>
                                    Ogni sede è rappresentata da una card collassabile con contatore <strong>disponibili/totali</strong>.
                                    Cliccando sull'intestazione si apre/chiude la tabella dei mezzi.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <div className="mezzi-disp-toolbar">
                <button
                    type="button"
                    className={`toggle-filters-btn ${showFilters ? "active" : ""}`}
                    onClick={() => setShowFilters((v) => !v)}
                >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
                    </svg>
                    {showFilters ? "Nascondi Filtri" : "Mostra Filtri"}
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="6 9 12 15 18 9" />
                    </svg>
                </button>

                <div className="mezzi-disp-toolbar-right">
                    <div className="mezzi-disp-summary">
                        <span className="mezzi-disp-summary-value">{totaleDisponibili}</span>
                        <span className="mezzi-disp-summary-sep">/</span>
                        <span className="mezzi-disp-summary-total">{totaleMezzi}</span>
                        <span className="mezzi-disp-summary-label">mezzi disponibili</span>
                    </div>
                    <div className="mezzi-disp-last-update">
                        <span className="mezzi-disp-live-dot" aria-hidden="true" />
                        Ultimo agg: <strong>{formatTime(lastUpdate)}</strong>
                    </div>
                </div>
            </div>

            {showFilters && (
                <div className="filter-panel">
                    <div className="filter-grid">
                        <div className="filter-group">
                            <label>Stato</label>
                            <Select
                                isMulti
                                placeholder="Tutti gli stati"
                                options={statoOptions}
                                value={selectedStati}
                                onChange={(selected) => setSelectedStati(selected || [])}
                                classNamePrefix="filter-select"
                                closeMenuOnSelect={false}
                            />
                        </div>
                        <div className="filter-group">
                            <label>Sede</label>
                            <Select
                                isMulti
                                placeholder="Tutte le sedi"
                                options={sedeOptions}
                                value={selectedSedi}
                                onChange={(selected) => setSelectedSedi(selected || [])}
                                classNamePrefix="filter-select"
                                closeMenuOnSelect={false}
                            />
                        </div>
                        <div className="filter-group">
                            <label>Tipologia</label>
                            <Select
                                isMulti
                                placeholder="Tutte le tipologie"
                                options={tipologiaOptions}
                                value={selectedTipologie}
                                onChange={(selected) => setSelectedTipologie(selected || [])}
                                classNamePrefix="filter-select"
                                closeMenuOnSelect={false}
                            />
                        </div>
                        <div className="filter-group">
                            <label>Sottotipologia</label>
                            <Select
                                isMulti
                                placeholder="Tutte le sottotipologie"
                                options={sottotipologiaOptions}
                                value={selectedSottotipologie}
                                onChange={(selected) => setSelectedSottotipologie(selected || [])}
                                classNamePrefix="filter-select"
                                closeMenuOnSelect={false}
                            />
                        </div>
                    </div>
                    <div className="mezzi-disp-filter-actions">
                        <button
                            type="button"
                            className="mezzi-disp-btn-primary"
                            onClick={handleApplyFiltri}
                        >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <polyline points="20 6 9 17 4 12" />
                            </svg>
                            Applica
                        </button>
                        <button
                            type="button"
                            className="mezzi-disp-btn-secondary"
                            onClick={handleResetFiltri}
                        >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
                                <path d="M3 3v5h5" />
                            </svg>
                            Reset
                        </button>
                    </div>
                </div>
            )}

            <div className="mezzi-disp-legend">
                {Object.entries(STATO_META).map(([key, meta]) => (
                    <span key={key} className="mezzi-disp-legend-item">
                        <span
                            className="mezzi-disp-badge"
                            style={{ background: meta.bg, color: meta.color, borderColor: meta.color }}
                        >
                            <span className="mezzi-disp-badge-icon" aria-hidden="true">{meta.icon}</span>
                            {meta.label}
                        </span>
                    </span>
                ))}
            </div>

            {cantieriUnici.length > 0 && (
                <div className="column-toggles">
                    <span className="column-toggle-label">Cantieri:</span>
                    <button
                        type="button"
                        className={`column-toggle-btn ${selectedCantieriChip.length === cantieriUnici.length ? "active" : ""}`}
                        onClick={() => {
                            if (selectedCantieriChip.length === cantieriUnici.length) {
                                setSelectedCantieriChip([]);
                            } else {
                                setSelectedCantieriChip(cantieriUnici);
                            }
                        }}
                    >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            {selectedCantieriChip.length === cantieriUnici.length ? (
                                <>
                                    <path d="M9 11l3 3L22 4"></path>
                                    <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"></path>
                                </>
                            ) : (
                                <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                            )}
                        </svg>
                        {selectedCantieriChip.length === cantieriUnici.length ? "Deseleziona tutti" : "Seleziona tutti"}
                    </button>
                    {cantieriUnici.map((cantiere) => (
                        <button
                            key={cantiere}
                            type="button"
                            className={`column-toggle-btn ${selectedCantieriChip.includes(cantiere) ? "active" : ""}`}
                            onClick={() => {
                                if (selectedCantieriChip.includes(cantiere)) {
                                    setSelectedCantieriChip(selectedCantieriChip.filter((c) => c !== cantiere));
                                } else {
                                    setSelectedCantieriChip([...selectedCantieriChip, cantiere]);
                                }
                            }}
                        >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                {selectedCantieriChip.includes(cantiere) ? (
                                    <>
                                        <path d="M9 11l3 3L22 4"></path>
                                        <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"></path>
                                    </>
                                ) : (
                                    <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                                )}
                            </svg>
                            {toTitleCase(cantiere)}
                        </button>
                    ))}
                </div>
            )}

            {tipologieUniche.length > 0 && (
                <div className="column-toggles">
                    <span className="column-toggle-label">Tipologia:</span>
                    <button
                        type="button"
                        className={`column-toggle-btn ${selectedTipologieChip.length === tipologieUniche.length ? "active" : ""}`}
                        onClick={() => {
                            if (selectedTipologieChip.length === tipologieUniche.length) {
                                setSelectedTipologieChip([]);
                            } else {
                                setSelectedTipologieChip(tipologieUniche);
                            }
                        }}
                    >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            {selectedTipologieChip.length === tipologieUniche.length ? (
                                <>
                                    <path d="M9 11l3 3L22 4"></path>
                                    <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"></path>
                                </>
                            ) : (
                                <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                            )}
                        </svg>
                        {selectedTipologieChip.length === tipologieUniche.length ? "Deseleziona tutti" : "Seleziona tutti"}
                    </button>
                    {tipologieUniche.map((tipologia) => (
                        <button
                            key={tipologia}
                            type="button"
                            className={`column-toggle-btn ${selectedTipologieChip.includes(tipologia) ? "active" : ""}`}
                            onClick={() => {
                                if (selectedTipologieChip.includes(tipologia)) {
                                    setSelectedTipologieChip(selectedTipologieChip.filter((t) => t !== tipologia));
                                } else {
                                    setSelectedTipologieChip([...selectedTipologieChip, tipologia]);
                                }
                            }}
                        >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                {selectedTipologieChip.includes(tipologia) ? (
                                    <>
                                        <path d="M9 11l3 3L22 4"></path>
                                        <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"></path>
                                    </>
                                ) : (
                                    <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                                )}
                            </svg>
                            {tipologia}
                        </button>
                    ))}
                </div>
            )}


            {error && (
                <div className="mezzi-disp-error" role="alert" style={{
                    padding: "12px 16px",
                    margin: "12px 0",
                    background: "#FEE2E2",
                    color: "#991B1B",
                    border: "1px solid #FCA5A5",
                    borderRadius: "8px",
                    fontSize: "14px",
                }}>
                    Errore nel recupero dati: {error}
                </div>
            )}

            {loading && sediData.length === 0 && !error && (
                <Lf23Loader />
            )}

            {!loading && !error && sediData.length === 0 && (
                <div style={{ padding: "24px", textAlign: "center", color: "#6B7280" }}>
                    Nessun mezzo disponibile.
                </div>
            )}

            <div className="mezzi-disp-list">
                {displayGroups.map((sede) => {
                    const isFullscreen = fullscreenSede === sede.sede;
                    const isFermiTable = !!sede.isFermiTable;
                    const isCollapsed = !isFullscreen && !isFermiTable && !!collapsedSedi[sede.sede];
                    return (
                        <section
                            key={sede.sede}
                            className={`mezzi-disp-sede-card ${isFullscreen ? "fullscreen-chart" : ""}`}
                        >
                            <RotateDevice active={isFullscreen} />
                            <div className="mezzi-disp-sede-header">
                                <button
                                    type="button"
                                    className="mezzi-disp-sede-header-toggle"
                                    onClick={() => !isFullscreen && !isFermiTable && toggleSede(sede.sede)}
                                    aria-expanded={!isCollapsed}
                                    disabled={isFullscreen || isFermiTable}
                                >
                                    <div className="mezzi-disp-sede-title">
                                        {!isFermiTable && (
                                            <svg
                                                className={`mezzi-disp-chevron ${isCollapsed ? "collapsed" : ""}`}
                                                width="16" height="16" viewBox="0 0 24 24" fill="none"
                                                stroke="currentColor" strokeWidth="2"
                                            >
                                                <polyline points="6 9 12 15 18 9" />
                                            </svg>
                                        )}
                                        <span className="mezzi-disp-sede-name">
                                            {isFermiTable ? sede.sede : toTitleCase(sede.sede)}
                                        </span>
                                    </div>
                                    <div className="mezzi-disp-sede-kpi">
                                        <span className="mezzi-disp-sede-count">
                                            {isFermiTable
                                                ? `${sede.totali} fermi`
                                                : `${sede.disponibili} disp / ${sede.totali} totali`}
                                        </span>
                                    </div>
                                </button>
                                <button
                                    type="button"
                                    className="mezzi-disp-fullscreen-btn"
                                    onClick={() => toggleFullscreen(sede.sede)}
                                    title={isFullscreen ? "Esci da tutto schermo" : "Tutto schermo"}
                                    aria-label={isFullscreen ? "Esci da tutto schermo" : "Tutto schermo"}
                                >
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        {isFullscreen ? (
                                            <>
                                                <polyline points="4 14 10 14 10 20"></polyline>
                                                <polyline points="20 10 14 10 14 4"></polyline>
                                                <line x1="14" y1="10" x2="21" y2="3"></line>
                                                <line x1="3" y1="21" x2="10" y2="14"></line>
                                            </>
                                        ) : (
                                            <>
                                                <polyline points="15 3 21 3 21 9"></polyline>
                                                <polyline points="9 21 3 21 3 15"></polyline>
                                                <line x1="21" y1="3" x2="14" y2="10"></line>
                                                <line x1="3" y1="21" x2="10" y2="14"></line>
                                            </>
                                        )}
                                    </svg>
                                </button>
                            </div>

                            {!isCollapsed && (
                                <div className="mezzi-disp-table-wrap">
                                    <table className="mezzi-disp-table">
                                        <thead>
                                            <tr>
                                                <th style={{ width: "10%" }}>Targa</th>
                                                <th style={{ width: "12%" }}>Sottotipologia</th>
                                                <th style={{ width: "12%" }}>Stato</th>
                                                <th style={{ width: "18%" }}>Motivo</th>
                                                <th style={{ width: "30%" }}>Timeline 18h (-6h / +12h)</th>
                                                <th style={{ width: "18%" }}>Posizione serale (19:30)</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {sede.mezzi.map((m) => {
                                                const meta = STATO_META[m.stato];
                                                const timeline = buildTimeline(m, lastUpdate);
                                                return (
                                                    <tr key={m.targa}>
                                                        <td className="mezzi-disp-targa">{m.targa}</td>
                                                        <td>{m.sottotipologia}</td>
                                                        <td>
                                                            <span
                                                                className="mezzi-disp-badge"
                                                                style={{
                                                                    background: meta.bg,
                                                                    color: meta.color,
                                                                    borderColor: meta.color,
                                                                }}
                                                            >
                                                                <span className="mezzi-disp-badge-icon" aria-hidden="true">
                                                                    {meta.icon}
                                                                </span>
                                                                {meta.label}
                                                            </span>
                                                        </td>
                                                        <td className="mezzi-disp-motivo">
                                                            {m.motivo || <span className="mezzi-disp-motivo-empty">—</span>}
                                                        </td>
                                                        <td className="mezzi-disp-timeline-cell">
                                                            <div
                                                                className="mezzi-disp-timeline"
                                                                role="img"
                                                                aria-label={`Timeline 18 ore per ${m.targa}`}
                                                            >
                                                                {timeline.map((slot, idx) => (
                                                                    <div key={idx} className="mezzi-disp-timeline-col">
                                                                        <span className={`mezzi-disp-timeline-hour ${slot.isNow ? "now" : ""}`}>
                                                                            {String(slot.hour).padStart(2, "0")}
                                                                        </span>
                                                                        <span
                                                                            className={`mezzi-disp-timeline-slot stato-${slot.stato} ${slot.isNow ? "now" : ""}`}
                                                                            title={`${String(slot.hour).padStart(2, "0")}:00 — ${STATO_META[slot.stato]?.label || slot.stato}`}
                                                                        />
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        </td>
                                                        <td className="mezzi-disp-posizione">
                                                            <span className="mezzi-disp-posizione-label">Posizione serale (19:30): </span>
                                                            {m.indirizzo ? (
                                                                <>
                                                                    {m.indirizzo}
                                                                    {m.posizione_at && (
                                                                        <span className="mezzi-disp-posizione-time">
                                                                            ultimo segnale {m.posizione_at}
                                                                        </span>
                                                                    )}
                                                                </>
                                                            ) : (
                                                                <span className="mezzi-disp-motivo-empty">—</span>
                                                            )}
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </section>
                    );
                })}
            </div>
        </div>
    );
};

export default MezziDisponibiliLive;
